/**
 * Deterministic + client helpers for applying amendment language to a main motion.
 */

/**
 * Try to apply free-text amendment phrasing without an LLM.
 * @returns {{ amendedText: string, confidence: 'high' | 'low', method: string } | null}
 */
export function tryDeterministicAmendment(currentMainText, amendmentText) {
  const main = String(currentMainText || '').trim();
  const amend = String(amendmentText || '').trim();
  if (!main || !amend) return null;

  const lower = amend.toLowerCase();

  // change/replace/substitute "X" with/to/for "Y"
  const replaceMatch = amend.match(
    /(?:change|replace|substitute)\s+["']([^"']+)["']\s+(?:with|to|for)\s+["']([^"']+)["']/i
  );
  if (replaceMatch) {
    const [, oldText, newText] = replaceMatch;
    if (main.toLowerCase().includes(oldText.toLowerCase())) {
      const amendedText = main.replace(new RegExp(escapeRegExp(oldText), 'gi'), newText);
      return { amendedText, confidence: 'high', method: 'replace' };
    }
  }

  // strike "X" and insert/replace with "Y"  OR  strike X and insert Y
  const strikeInsertQuoted = amend.match(
    /strike(?:\s+out)?\s+["']([^"']+)["']\s+(?:and\s+)?(?:insert|add|replace(?:\s+with)?|substitute)\s+["']([^"']+)["']/i
  );
  if (strikeInsertQuoted) {
    const [, oldText, newText] = strikeInsertQuoted;
    if (main.toLowerCase().includes(oldText.toLowerCase())) {
      const amendedText = main.replace(new RegExp(escapeRegExp(oldText), 'gi'), newText);
      return { amendedText, confidence: 'high', method: 'strike_insert' };
    }
  }

  // strike "X" (delete only)
  const strikeOnly = amend.match(/strike(?:\s+out)?\s+(?:the\s+words?\s+)?["']([^"']+)["']/i);
  if (strikeOnly && !/\b(insert|add|replace|substitute)\b/i.test(amend)) {
    const [, oldText] = strikeOnly;
    if (main.toLowerCase().includes(oldText.toLowerCase())) {
      const amendedText = main
        .replace(new RegExp(escapeRegExp(oldText), 'gi'), '')
        .replace(/\s{2,}/g, ' ')
        .trim();
      return { amendedText, confidence: 'high', method: 'strike' };
    }
  }

  // insert/add "Y" after "X"
  const insertAfter = amend.match(
    /(?:insert|add)\s+["']([^"']+)["']\s+after\s+(?:the\s+words?\s+)?["']([^"']+)["']/i
  );
  if (insertAfter) {
    const [, insertText, afterText] = insertAfter;
    const idx = main.toLowerCase().indexOf(afterText.toLowerCase());
    if (idx >= 0) {
      const end = idx + afterText.length;
      const amendedText = `${main.slice(0, end)} ${insertText}${main.slice(end)}`.replace(
        /\s{2,}/g,
        ' '
      );
      return { amendedText, confidence: 'high', method: 'insert_after' };
    }
  }

  // insert/add "Y" at the end / at end
  const insertEnd = amend.match(
    /(?:insert|add)\s+["']([^"']+)["']\s+(?:at\s+the\s+end|to\s+the\s+end)/i
  );
  if (insertEnd) {
    const [, insertText] = insertEnd;
    const amendedText = `${main.replace(/[.!?]?$/, '')} ${insertText}`.trim();
    return { amendedText, confidence: 'high', method: 'insert_end' };
  }

  // Unquoted change X to Y (lower confidence)
  if (
    lower.includes('change') ||
    lower.includes('replace') ||
    lower.includes('substitute')
  ) {
    const loose = amend.match(
      /(?:change|replace|substitute)\s+(.+?)\s+(?:with|to|for)\s+(.+)$/i
    );
    if (loose) {
      const oldText = loose[1].replace(/^["']|["']$/g, '').trim();
      const newText = loose[2].replace(/^["']|["']$/g, '').trim();
      if (oldText && newText && main.toLowerCase().includes(oldText.toLowerCase())) {
        const amendedText = main.replace(new RegExp(escapeRegExp(oldText), 'gi'), newText);
        return { amendedText, confidence: 'low', method: 'replace_loose' };
      }
    }
  }

  return null;
}

function escapeRegExp(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Apply a confirmed amended text onto the main motion object.
 */
export function applyPassedAmendmentToMain(mainMotion, amendmentMotion, amendedText, result = 'passed') {
  const nextText =
    typeof amendedText === 'string' && amendedText.trim()
      ? amendedText.trim()
      : mainMotion.text;

  return {
    ...mainMotion,
    text: nextText,
    originalText: mainMotion.originalText || mainMotion.text,
    status: 'open_debate',
    amendedBy: amendmentMotion.member,
    amendedAt: new Date().toLocaleTimeString(),
    isAmended: true,
    amendments: [
      ...(mainMotion.amendments || []),
      {
        text: amendmentMotion.text,
        member: amendmentMotion.member,
        timestamp: new Date().toLocaleTimeString(),
        result,
        amendedText: nextText,
      },
    ],
  };
}

/**
 * Resolve a proposed amended main-motion text: deterministic first, then API.
 */
export async function proposeAmendedMainText({
  currentMainText,
  originalMainText,
  amendmentText,
  member,
  accessToken,
  organizationId,
}) {
  const det = tryDeterministicAmendment(currentMainText, amendmentText);
  if (det && det.confidence === 'high') {
    return {
      amendedText: det.amendedText,
      summary: `Applied amendment (${det.method})`,
      source: 'deterministic',
    };
  }

  try {
    const headers = { 'Content-Type': 'application/json' };
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

    const res = await fetch('/api/apply-amendment', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        currentMainText,
        originalMainText,
        amendmentText,
        organizationId,
        member,
      }),
    });

    if (res.ok) {
      const body = await res.json();
      if (body?.amendedText && String(body.amendedText).trim()) {
        return {
          amendedText: String(body.amendedText).trim(),
          summary: body.summary || 'AI proposed amended text',
          source: 'ai',
        };
      }
    }
  } catch (err) {
    console.warn('[amendmentText] API apply failed', err?.message || err);
  }

  // Fallback: deterministic low-confidence, else current text for manual edit
  if (det?.amendedText) {
    return {
      amendedText: det.amendedText,
      summary: `Suggested from pattern (${det.method}) — please verify`,
      source: 'deterministic',
    };
  }

  return {
    amendedText: String(currentMainText || '').trim(),
    summary:
      'Could not auto-apply the amendment. Edit the main motion text below to match the amendment that passed.',
    source: 'manual',
  };
}
